import { api, stepUpHeaders, type StepUpProof } from './api';
import { STEP_UP_PURPOSES_AGENT_PROPOSAL, type PasskeyStepUpTarget } from './passkeys';

/**
 * Propostas de agentes (change platform-mcp-writes, tarefa 1.19): o cliente das rotas
 * `/api/v1/platform/agent-proposals/**` e as regras puras da página "Propostas de agentes".
 *
 * Aprovar exige o step-up amarrado à proposta (TOTP para prazo e acesso, passkey para dinheiro e
 * perda de acesso; a API decide pelo tipo gravado) e o `viewedDiffHash` do diff que a pessoa viu:
 * se a proposta mudou depois da leitura, a API responde 409 `diff_changed` e a tela recarrega. Sem
 * lote: uma proposta por vez. Nenhuma rota usa `Idempotency-Key`; a segunda aprovação da mesma
 * proposta acha `Executing` (202) ou o estado final (409).
 */

const AGENT_PROPOSALS = '/platform/agent-proposals';

export type AgentProposalStatus =
  | 'Pending'
  | 'Withdrawn'
  | 'Executing'
  | 'Executed'
  | 'Rejected'
  | 'Expired'
  | 'StateChanged'
  | 'RuleViolated'
  | 'CancelledByRevocation'
  | 'Superseded'
  | 'Unsupported'
  | 'NeedsReconciliation'
  | 'Reverted';

export type AgentProposalStepUp = 'totp' | 'passkey';

export interface AgentProposalListItem {
  id: string;
  kind: string;
  kindLabel: string;
  status: AgentProposalStatus;
  tenantId: string;
  tenantShortId: string;
  createdAt: string;
  expiresAt: string;
  originSuperseded: boolean;
  agentProfile: string;
  requiredStepUp: AgentProposalStepUp;
}

export interface AgentProposalStore {
  found: boolean;
  tenantShortId: string;
  city: string | null;
  state: string | null;
  createdAt: string | null;
  isActive: boolean | null;
  subscriptionStatus: string | null;
  commercialVersion: number | null;
  confusableName: boolean;
  /** Nome informado pela loja: texto de terceiro, mostrado depois da loja verificada e rotulado. */
  tradeName: string | null;
}

export interface AgentProposalDiffLine {
  field: string;
  expected: string | null;
  requested: string | null;
}

export interface AgentProposalRelated {
  id: string;
  kind: string;
  status: AgentProposalStatus;
  createdAt: string;
  executedAt: string | null;
}

export interface AgentProposalDetail {
  id: string;
  stepUpTargetId: string;
  kind: string;
  kindLabel: string;
  status: AgentProposalStatus;
  store: AgentProposalStore;
  origin: {
    agentProfile: string;
    oauthClientId: string;
    agentGrantId: string;
    agentGrantStatus: string;
    onBehalfOfAccountId: string;
    onBehalfOfName: string | null;
    workOrderId: string;
    workOrderStatus: string;
  };
  diff: AgentProposalDiffLine[];
  diffHash: string;
  expectedStateVersion: number;
  evidence: unknown;
  evidenceAsOf: string | null;
  evidenceStale: boolean;
  reasonCode: string;
  /** Texto da IA: dado, nunca instrução. A tela o rotula. */
  reasonDetail: string | null;
  pendingForStore: AgentProposalRelated[];
  recentEffects: AgentProposalRelated[];
  createdAt: string;
  expiresAt: string;
  originSupersededAt: string | null;
  approvedByAccountId: string | null;
  approvedAt: string | null;
  executedAt: string | null;
  rejectedReasonCode: string | null;
  requiredStepUp: AgentProposalStepUp;
  canDecide: boolean;
  selfApproval: boolean;
  selfApprovalRefusal: string | null;
}

export interface AgentProposalDecision {
  id: string;
  status: AgentProposalStatus;
  receiptId: string | null;
}

export async function listAgentProposals(status?: AgentProposalStatus): Promise<AgentProposalListItem[]> {
  const { data } = await api.get<AgentProposalListItem[]>(AGENT_PROPOSALS, { params: { status: status || undefined } });
  return data;
}

export async function getAgentProposal(id: string): Promise<AgentProposalDetail> {
  const { data } = await api.get<AgentProposalDetail>(`${AGENT_PROPOSALS}/${encodeURIComponent(id)}`);
  return data;
}

/** Aprova com o step-up e o hash do diff visto; 202 (`Executing`) também volta aqui. */
export async function approveAgentProposal(
  id: string,
  payload: { viewedDiffHash: string; confirmSupersededOrigin?: boolean },
  stepUp: StepUpProof,
): Promise<AgentProposalDecision> {
  const { data } = await api.post<AgentProposalDecision>(
    `${AGENT_PROPOSALS}/${encodeURIComponent(id)}/approve`,
    { viewedDiffHash: payload.viewedDiffHash, confirmSupersededOrigin: payload.confirmSupersededOrigin || undefined },
    { headers: stepUpHeaders(stepUp) },
  );
  return data;
}

export async function rejectAgentProposal(id: string, reasonCode: AgentProposalRejectReason): Promise<AgentProposalDecision> {
  const { data } = await api.post<AgentProposalDecision>(`${AGENT_PROPOSALS}/${encodeURIComponent(id)}/reject`, { reasonCode });
  return data;
}

export async function revertAgentProposal(
  id: string,
  payload: { viewedDiffHash: string },
  stepUp: StepUpProof,
): Promise<AgentProposalDecision> {
  const { data } = await api.post<AgentProposalDecision>(
    `${AGENT_PROPOSALS}/${encodeURIComponent(id)}/revert`,
    { viewedDiffHash: payload.viewedDiffHash },
    { headers: stepUpHeaders(stepUp) },
  );
  return data;
}

/** Motivos de recusa da lista fechada da API (`PlatformAgentProposalHandler.RejectReasons`). */
export type AgentProposalRejectReason = 'not_needed' | 'wrong_store' | 'amount_too_high' | 'suspicious' | 'other';

export const REJECT_REASONS: ReadonlyArray<{ value: AgentProposalRejectReason; label: string }> = [
  { value: 'not_needed', label: 'Não é necessário' },
  { value: 'wrong_store', label: 'Loja errada' },
  { value: 'amount_too_high', label: 'Valor ou prazo acima do razoável' },
  { value: 'suspicious', label: 'Suspeita (avisar a equipe)' },
  { value: 'other', label: 'Outro' },
];

const STATUS_LABELS: Record<AgentProposalStatus, string> = {
  Pending: 'Pendente',
  Withdrawn: 'Retirada pelo agente',
  Executing: 'Em execução',
  Executed: 'Executada',
  Rejected: 'Recusada',
  Expired: 'Vencida',
  StateChanged: 'Estado mudou',
  RuleViolated: 'Regra recusou',
  CancelledByRevocation: 'Cancelada por revogação',
  Superseded: 'Superada',
  Unsupported: 'Não suportada',
  NeedsReconciliation: 'Precisa de conciliação',
  Reverted: 'Revertida',
};

export function proposalStatusLabel(status: string): string {
  return STATUS_LABELS[status as AgentProposalStatus] ?? status;
}

/** O alvo do desafio de passkey: a própria proposta, pelo id em uuid que a API devolve. */
export function proposalPasskeyTarget(
  action: 'approve' | 'revert',
  stepUpTargetId: string,
): PasskeyStepUpTarget {
  return { ...STEP_UP_PURPOSES_AGENT_PROPOSAL[action], targetId: stepUpTargetId };
}

/** A mensagem de cada recusa da página, por `errorCode`. */
export const PROPOSAL_ERROR_MESSAGES: Readonly<Record<string, string>> = {
  'platform.agent_proposal.diff_changed':
    'A proposta mudou desde que você a abriu. A tela foi recarregada: confira o diff antes de aprovar.',
  'platform.agent_proposal.not_pending': 'A proposta não está mais pendente.',
  'platform.agent_proposal.expired': 'A proposta venceu sem decisão; nada mudou.',
  'platform.agent_proposal.writes_disabled':
    'A escrita pelo agente está desligada agora; a proposta segue pendente e nada mudou.',
  'platform.agent_proposal.unsupported': 'Esta versão ainda não executa este tipo de proposta; nada mudou.',
  'platform.agent_proposal.state_changed': 'O estado da loja mudou desde a proposta; nada foi aplicado.',
  'platform.agent_proposal.rule_violated': 'Uma regra ou um teto recusou a execução; nada foi aplicado.',
  'platform.agent_proposal.grant_revoked': 'A autorização que criou a proposta foi revogada; ela foi cancelada.',
  'platform.agent_proposal.permission_required': 'Seu papel não tem a permissão deste tipo de proposta.',
  'platform.agent_proposal.self_approval_forbidden':
    'Quem consentiu a autorização não aprova dinheiro nem perda de acesso quando outra pessoa pode aprovar.',
  'platform.agent_proposal.self_approval_too_early':
    'Autoaprovação de dinheiro ou perda de acesso só 30 minutos depois da criação da proposta.',
  'platform.agent_proposal.origin_superseded_confirmation_required':
    'A autorização que criou esta proposta foi substituída: marque a confirmação extra.',
  'platform.agent_proposal.revert_unsupported': 'Esta versão ainda não reverte este tipo de proposta.',
};

/** Só pendente e no prazo pode ser decidida. */
export function isDecidable(detail: Pick<AgentProposalDetail, 'status' | 'canDecide'>): boolean {
  return detail.status === 'Pending' && detail.canDecide;
}
